import React, { useState, useCallback, useMemo } from "react";
import {
  Flex, Accordion, Tabs, TabItem, TextField,
} from "@wingmate/toolkit";
import { useTranslation } from "react-i18next";
import { IContactAttributes } from "../../../types/IContact";
import { ICustomField } from "../../../types/ICustomField";
import { IPayload } from "../CreateForm";

type IContactTab = Omit<
IContactAttributes,
"createdAt" | "updatedAt" | "primaryContact"
>;

export interface IContactSectionProps {
  fieldId: string;
  contactFields: ICustomField[];
  onChange: (data: { id: string; value: Array<IContactTab> }) => void;
  payload: IPayload;
}

export const ContactSection: React.FC<IContactSectionProps> = ({
  contactFields,
  fieldId,
  onChange,
  payload,
}) => {
  const { t } = useTranslation(["default", "common"]);
  const [activeKey, setActiveKey] = useState<string>();

  const contacts = useMemo(() => (payload[fieldId] as IContactTab[]) || [], [payload, fieldId]);

  const handleAddContact = useCallback(() => {
    const lastId = contacts[contacts.length - 1]?.id || 0;
    const newId = lastId + 1;

    const newContact: IContactTab = {
      id: newId,
      name: "",
      email: "",
      customFields: {},
    };
    const newContacts = [...contacts, newContact];
    onChange({ id: fieldId, value: newContacts });

    setActiveKey(newId.toString());
  }, [fieldId, onChange, contacts]);

  const handleRemoveContact = useCallback(
    (id: string | React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
      const newContacts = contacts.filter(
        (contact) => contact.id.toString() !== id,
      );
      onChange({ id: fieldId, value: newContacts });

      if (activeKey === id) {
        const currentIndex = contacts.findIndex((c) => c.id.toString() === id);
        const newActiveIndex = Math.max(0, currentIndex - 1);
        setActiveKey(contacts[newActiveIndex]?.id.toString() || "");
      }
    },
    [activeKey, fieldId, onChange, contacts],
  );

  const onEditTab = useCallback(
    (
      _tabItems: TabItem[],
      targetKey:
      | string
      | React.MouseEvent<Element>
      | React.KeyboardEvent<Element>,
      action: "add" | "remove",
    ) => {
      if (action === "add") {
        handleAddContact();
      } else if (action === "remove") {
        handleRemoveContact(targetKey);
      }
    },
    [handleAddContact, handleRemoveContact],
  );

  const updateContact = useCallback(
    (id: number, updates: Partial<IContactTab>) => {
      const updatedContacts = contacts.map((contact) => {
        if (contact.id === id) {
          return { ...contact, ...updates };
        }
        return contact;
      });
      onChange({ id: fieldId, value: updatedContacts });
    },
    [fieldId, onChange, contacts],
  );

  const handleFieldChange = useCallback(
    (id: number, field: string, data: { value?: string | number }) => {
      if (data.value !== undefined) {
        updateContact(id, { [field]: String(data.value) });
      }
    },
    [updateContact],
  );

  const handleCustomFieldChange = useCallback(
    (id: number, field: string, data: { value?: string | number }) => {
      if (data.value !== undefined) {
        const updatedContacts = contacts.map((contact) => {
          if (contact.id === id) {
            return {
              ...contact,
              customFields: {
                ...contact.customFields,
                [field]: String(data.value),
              },
            };
          }
          return contact;
        });
        onChange({ id: fieldId, value: updatedContacts });
      }
    },
    [fieldId, onChange, contacts],
  );

  const renderContactTab = (contact: IContactTab) => (
    <Flex key={contact.id} vertical gap={16}>
      <Flex vertical>
        <TextField
          id={`name-${contact.id}`}
          data-testid={`ContactSection__name-${contact.id}`}
          label={t("common:name")}
          onChange={(data) => handleFieldChange(contact.id, "name", data)}
          defaultValue={contact.name}
          required
        />
      </Flex>
      <Flex vertical>
        <TextField
          id={`email-${contact.id}`}
          data-testid={`ContactSection__email-${contact.id}`}
          type="email"
          label={t("common:user.fields.email")}
          onChange={(data) => handleFieldChange(contact.id, "email", data)}
          defaultValue={contact.email}
        />
      </Flex>

      {contactFields.map((field) => (
        <Flex key={field.id} vertical>
          <TextField
            id={`contact-${contact.id}-custom-${field.id}`}
            label={field.name}
            onChange={(data) => {
              handleCustomFieldChange(contact.id, field.id, data);
            }}
            defaultValue={contact.customFields[field.id]}
          />
        </Flex>
      ))}
    </Flex>
  );

  const items = contacts.map((contact, index) => ({
    key: contact.id.toString(),
    label: contact.name?.trim() || `Contact ${index + 1}`,
    children: renderContactTab(contact),
  }));

  const renderContactTabs = () => (
    <Tabs
      data-testid="ContactSection__Tabs"
      items={items}
      activeKey={activeKey?.toString()}
      onTabClick={(key) => setActiveKey(key)}
      type="editable-card"
      onEdit={onEditTab}
    />
  );

  return (
    <Flex align="stretch" vertical className="CreateForm__section">
      <Accordion
        header={t("common:contact.contacts")}
        content={renderContactTabs()}
        defaultExpanded
      />
    </Flex>
  );
};

export default ContactSection;
